/**
 * Web Widget Settings — canonical type for the embeddable Lit web component.
 *
 * Persisted in `agent.web_widget_settings` (jsonb) and consumed by both the
 * agent-builder UI (`apps/web/lib/agent-builder/web-widget-settings.ts`) and the
 * Lit web component (`packages/embed-widget`).
 *
 * Mirrors the previous app-local definition; the app-local module re-exports
 * this type to maintain backwards compatibility while consolidating the
 * canonical shape in shared-types.
 */

export type WebWidgetAvatarMode = "default" | "upload";
export type WebWidgetSize = "small" | "medium" | "large";
export type WebWidgetPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "center";

/**
 * Colour scheme of the widget.
 *
 * "auto" follows the visitor's OS preference, which is the right default but
 * the wrong answer for a site that is itself always dark or always light: the
 * widget then clashes with the page it sits on. The owner can pin it.
 */
export type WebWidgetTheme = "auto" | "light" | "dark";

/** How round the widget's corners are: DeepAgent's 2px, softened, or pill buttons. */
export type WebWidgetCorners = "square" | "soft" | "round";

export const WEB_WIDGET_CORNERS: readonly WebWidgetCorners[] = [
  "square",
  "soft",
  "round",
];

/** DeepAgent's Inter, or whatever font the host page already uses. */
export type WebWidgetFont = "default" | "site";

export const WEB_WIDGET_FONTS: readonly WebWidgetFont[] = ["default", "site"];

/**
 * What makes the widget speak first.
 *
 * "delay" counts seconds on the page, "scroll" waits for a share of the page to
 * be read, "exit-intent" fires when the pointer leaves towards the browser
 * chrome. One trigger at a time: combining them produces a widget that nags,
 * and the owner cannot reason about which rule fired.
 */
export type WebWidgetProactiveTrigger = "delay" | "scroll" | "exit-intent";

/**
 * How a visitor may talk to the agent.
 *
 * "voice" is the widget as it has always been. "text" is a chat: no
 * microphone, no audio, the agent answers in writing. "voice_text" offers both
 * entry points on the launcher; each session is one or the other, never a
 * switch in the middle.
 */
export type WebWidgetModality = "voice" | "voice_text" | "text";

export const WEB_WIDGET_MODALITIES: readonly WebWidgetModality[] = [
  "voice",
  "voice_text",
  "text",
];

export function isWebWidgetModality(
  value: unknown,
): value is WebWidgetModality {
  return value === "voice" || value === "voice_text" || value === "text";
}

/**
 * The modality stored on a widget, read straight off the raw settings.
 *
 * Every reader of `web_widget_settings` (the session endpoint, the agents
 * list, the builder, the provider mapper) needs only this one field, and a
 * full {@link mergeWebWidgetSettings} per row was the price of not having a
 * narrower reader. Same guard as the merge: anything but the three known
 * values, including no settings at all, reads as "voice".
 */
export function readWebWidgetModality(settings: unknown): WebWidgetModality {
  const modality =
    settings && typeof settings === "object"
      ? (settings as { modality?: unknown }).modality
      : undefined;
  return isWebWidgetModality(modality) ? modality : "voice";
}

/** What a single session is: a call, or a written chat. */
export type SessionModality = "voice" | "text";

/**
 * A widget in "voice" must not open chats and one in "text" must not open
 * calls, even if a client asks: the launcher only offers what is allowed, so
 * a mismatch here is a stale bundle or a hand-crafted request.
 */
export function widgetAllowsSession(
  widget: WebWidgetModality,
  session: SessionModality,
): boolean {
  return session === "text" ? widget !== "voice" : widget !== "text";
}

export interface WebWidgetProactive {
  enabled: boolean;
  trigger: WebWidgetProactiveTrigger;
  /** Seconds on the page before the bubble appears. Read by the "delay" trigger. */
  delaySeconds: number;
  /** Share of the page scrolled, 1-100, before it appears. Read by "scroll". */
  scrollPercent: number;
}

export interface WebWidgetAvatar {
  mode: WebWidgetAvatarMode;
  url?: string;
}

export interface WebWidgetTerms {
  enabled: boolean;
  content: string;
}

export interface WebWidgetTextContents {
  mainLabel: string;
  startCall: string;
  endCall: string;
  changeLanguage: string;
  muteMicrophone: string;
  acceptTerms: string;
  dismissTerms: string;
  /**
   * Greeting in the proactive bubble. Lives here rather than on
   * {@link WebWidgetProactive} so it inherits the per-locale defaults and the
   * merge every other piece of widget copy already gets; `proactive` stays
   * purely about when to speak, never about what to say.
   */
  proactiveMessage: string;
  /** Launcher / footer action that opens a chat session. */
  startChat: string;
  /** Placeholder of the chat composer. */
  composerPlaceholder: string;
  /** Label of the send button. */
  send: string;
  /** Shown while the visitor waits for the agent's written reply. */
  agentTyping: string;
}

/**
 * The accent, and optionally the surface. The widget derives text, borders and
 * secondary surfaces from these two, so the owner can never pick an unreadable pair.
 */
export interface WebWidgetColors {
  primary: string;
  /** The panel and launcher surface; absent follows the light or dark theme. */
  background?: string;
}

export interface WebWidgetSettings {
  avatar: WebWidgetAvatar;
  terms: WebWidgetTerms;
  size: WebWidgetSize;
  collapsible: boolean;
  position: WebWidgetPosition;
  realtimeTranscript: boolean;
  languageSelector: boolean;
  /** Default language for all widget UI; absent on legacy widgets. */
  locale?: WebWidgetLocale;
  /** "Powered by DeepAgent" footer in the expanded panel. Defaults on. */
  showBranding: boolean;
  theme: WebWidgetTheme;
  corners: WebWidgetCorners;
  font: WebWidgetFont;
  modality: WebWidgetModality;
  proactive: WebWidgetProactive;
  textContents: WebWidgetTextContents;
  colors: WebWidgetColors;
}

/**
 * Locales the widget ships copy for. Matches both the platform UI languages
 * and the embed bundle's own dictionary.
 */
export type WebWidgetLocale = "en" | "it" | "es";

/** Stock labels follow the widget locale; custom wording remains unchanged. */
export const WEB_WIDGET_TEXT_CONTENTS_BY_LOCALE: Record<
  WebWidgetLocale,
  WebWidgetTextContents
> = {
  en: {
    mainLabel: "Talk to us",
    startCall: "Call",
    endCall: "End",
    changeLanguage: "Change language",
    muteMicrophone: "Mute microphone",
    acceptTerms: "Accept",
    dismissTerms: "Close",
    proactiveMessage: "Have a question? Let's talk.",
    startChat: "Write to us",
    composerPlaceholder: "Write a message…",
    send: "Send",
    agentTyping: "Writing…",
  },
  it: {
    mainLabel: "Parla con noi",
    startCall: "Chiama",
    endCall: "Termina",
    changeLanguage: "Cambia lingua",
    muteMicrophone: "Disattiva microfono",
    acceptTerms: "Accetta",
    dismissTerms: "Chiudi",
    proactiveMessage: "Hai una domanda? Parliamone.",
    startChat: "Scrivici",
    composerPlaceholder: "Scrivi un messaggio…",
    send: "Invia",
    agentTyping: "Sta scrivendo…",
  },
  es: {
    mainLabel: "Habla con nosotros",
    startCall: "Llamar",
    endCall: "Finalizar",
    changeLanguage: "Cambiar idioma",
    muteMicrophone: "Silenciar micrófono",
    acceptTerms: "Aceptar",
    dismissTerms: "Cerrar",
    proactiveMessage: "¿Tienes alguna pregunta? Hablemos.",
    startChat: "Escríbenos",
    composerPlaceholder: "Escribe un mensaje…",
    send: "Enviar",
    agentTyping: "Escribiendo…",
  },
};

/** Stock wording earlier releases saved into widgets, still read as untouched. */
const RETIRED_TEXT_CONTENTS: Record<
  WebWidgetLocale,
  Partial<WebWidgetTextContents>
> = {
  en: { startCall: "Start call" },
  it: { startCall: "Avvia chiamata" },
  es: { startCall: "Iniciar llamada" },
};

/**
 * True when `value` is `locale`'s stock wording for `key`, current or retired.
 * A text-only widget's launcher reads the chat label, so that counts too.
 */
export function isLocaleStockText(
  locale: WebWidgetLocale,
  key: keyof WebWidgetTextContents,
  value: unknown,
): boolean {
  const stock = WEB_WIDGET_TEXT_CONTENTS_BY_LOCALE[locale];
  return (
    value === stock[key] ||
    value === RETIRED_TEXT_CONTENTS[locale][key] ||
    (key === "mainLabel" && value === stock.startChat)
  );
}

export const WEB_WIDGET_LOCALES: readonly WebWidgetLocale[] = ["en", "it", "es"];

export function isWebWidgetLocale(value: unknown): value is WebWidgetLocale {
  return value === "en" || value === "it" || value === "es";
}

/**
 * Default widget copy for a locale, falling back to English for anything the
 * bundle has no wording for. Accepts a full tag ("it-IT") as well as a code.
 */
export function getDefaultWebWidgetTextContents(
  locale: string | null | undefined,
): WebWidgetTextContents {
  const code = locale?.slice(0, 2).toLowerCase();
  return isWebWidgetLocale(code)
    ? WEB_WIDGET_TEXT_CONTENTS_BY_LOCALE[code]
    : WEB_WIDGET_TEXT_CONTENTS_BY_LOCALE.en;
}

/** Translate stock labels across locales while preserving the operator's own copy. */
export function localizeWebWidgetTextContents(
  contents: Partial<WebWidgetTextContents> | undefined,
  locale: WebWidgetLocale,
  modality: WebWidgetModality = "voice",
): WebWidgetTextContents {
  const defaults = getDefaultWebWidgetTextContents(locale);
  const result = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof WebWidgetTextContents)[]) {
    const value = contents?.[key];
    const isDefault = WEB_WIDGET_LOCALES.some((stockLocale) =>
      isLocaleStockText(stockLocale, key, value),
    );
    result[key] =
      typeof value === "string" && !isDefault ? value : defaults[key];
    if (
      key === "mainLabel" &&
      (typeof value !== "string" || isDefault) &&
      modality === "text"
    ) {
      result[key] = defaults.startChat;
    }
  }
  return result;
}

export const DEFAULT_WEB_WIDGET_SETTINGS: WebWidgetSettings = {
  // No picture: the widget shows the agent matrix, DeepAgent's own mark.
  avatar: { mode: "default" },
  terms: { enabled: false, content: "" },
  size: "medium",
  collapsible: true,
  position: "bottom-right",
  realtimeTranscript: true,
  languageSelector: true,
  showBranding: true,
  theme: "auto",
  corners: "square",
  font: "default",
  modality: "voice",
  // Off by default on purpose: a widget that speaks first without the owner
  // asking for it is a behaviour change on their visitors, not a new feature.
  proactive: {
    enabled: false,
    trigger: "delay",
    delaySeconds: 20,
    scrollPercent: 50,
  },
  colors: { primary: "#456CE8" },
  textContents: WEB_WIDGET_TEXT_CONTENTS_BY_LOCALE.en,
};

/**
 * Merge stored widget settings over the defaults.
 *
 * The only place that knows how deep the merge has to go. Both the builder
 * (reading the draft out of its store) and the public embed config endpoint
 * (reading the `agent.web_widget_settings` jsonb) call this, because they used
 * to carry a copy each — and a copy means every new nested field has to be
 * remembered twice, or it silently loses its defaults on one of the two paths.
 *
 * `stored` is deliberately `unknown`: it arrives as jsonb on one path and as a
 * typed partial on the other, and neither is trustworthy enough to spread blind.
 *
 * `locale` decides only which wording an *untouched* widget starts from, so an
 * operator working in Italian is not handed English labels to translate by hand.
 * Anything already stored wins: a saved agent is never re-localized behind them.
 */
export function mergeWebWidgetSettings(
  stored: unknown,
  locale?: string | null,
): WebWidgetSettings {
  const partial: Partial<WebWidgetSettings> =
    stored && typeof stored === "object"
      ? (stored as Partial<WebWidgetSettings>)
      : {};

  const widgetLocale = isWebWidgetLocale(partial.locale)
    ? partial.locale
    : undefined;
  const defaultTextContents =
    widgetLocale || locale
      ? getDefaultWebWidgetTextContents(widgetLocale || locale)
      : DEFAULT_WEB_WIDGET_SETTINGS.textContents;

  return {
    ...DEFAULT_WEB_WIDGET_SETTINGS,
    ...partial,
    locale: widgetLocale,
    // Guarded rather than spread: a stale or hand-edited jsonb must not put
    // the widget in a state the launcher has no buttons for.
    modality: isWebWidgetModality(partial.modality)
      ? partial.modality
      : DEFAULT_WEB_WIDGET_SETTINGS.modality,
    avatar: withoutRetiredAvatar({
      ...DEFAULT_WEB_WIDGET_SETTINGS.avatar,
      ...partial.avatar,
    }),
    terms: { ...DEFAULT_WEB_WIDGET_SETTINGS.terms, ...partial.terms },
    corners: WEB_WIDGET_CORNERS.find((value) => value === partial.corners) ??
      DEFAULT_WEB_WIDGET_SETTINGS.corners,
    font: WEB_WIDGET_FONTS.find((value) => value === partial.font) ??
      DEFAULT_WEB_WIDGET_SETTINGS.font,
    colors: { ...DEFAULT_WEB_WIDGET_SETTINGS.colors, ...partial.colors },
    proactive: {
      ...DEFAULT_WEB_WIDGET_SETTINGS.proactive,
      ...partial.proactive,
    },
    textContents: { ...defaultTextContents, ...partial.textContents },
  };
}

/**
 * The old default avatar was Lara's name wordmark, which a square or round
 * frame cuts in half. Saved widgets still carry it: they get the matrix too.
 */
function withoutRetiredAvatar(avatar: WebWidgetAvatar): WebWidgetAvatar {
  return avatar.url === "/voices/names/lara.png" ? { mode: "default" } : avatar;
}
